import { Box, Button } from '@factorialco/deprecated-design-system'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'

import { useMarkAsPaidPaymentExecution } from 'modules/banking/hooks/useMarkAsPaidPaymentExecution'
import { useRemoveBulkPayment } from 'modules/banking/hooks/useRemoveBulkPayment'
import { useUpdateSepaFile } from 'modules/banking/hooks/useUpdateSepaFile'
import { lastPaymentExecution } from 'modules/banking/lib/paymentHelpers'

import type { Payment } from './types'

interface PaymentActionsProps {
  payment: Payment
}

interface DownloadSepaActionProps {
  paymentExecution: Payment['paymentExecutions'][number]
}

const DownloadSepaAction = ({ paymentExecution }: DownloadSepaActionProps) => {
  const { updateSepaFile } = useUpdateSepaFile()
  const label = i18n.t('banking.payments.download_sepa_file')

  if (paymentExecution.sepaFile?.url) {
    return <PopoverMenu.HrefOption href={paymentExecution.sepaFile.url} title={label} download />
  } else {
    return (
      <PopoverMenu.TouchableOption
        title={label}
        onClick={() => updateSepaFile(paymentExecution.id)}
      />
    )
  }
}

export const Actions = ({ payment }: PaymentActionsProps) => {
  const { navigation } = useNavigation()
  const paymentExecution = lastPaymentExecution(payment)
  const { submitRemoveBulkPayment } = useRemoveBulkPayment()
  const { markAsPaidPaymentExecution } = useMarkAsPaidPaymentExecution()

  const onRemoveBulkPayment = async () => {
    await submitRemoveBulkPayment({ paymentId: payment.id })
  }

  const onMarkAsPaid = async () => {
    if (paymentExecution) {
      await markAsPaidPaymentExecution(paymentExecution.id)
    }
  }

  const transfersCount = paymentExecution?.transfersConnection.totalCount || 0

  return (
    <Box gap="s4" flexDirection="row" justifyContent="flexEnd">
      <PopoverMenu type="list">
        <PopoverMenu.Trigger>
          <Button.Decorative type="secondary" iconName="ellipsis" />
        </PopoverMenu.Trigger>
        <PopoverMenu.Content side="bottom" align="start">
          {(!paymentExecution || !transfersCount) && (
            <PopoverMenu.TouchableOption
              title={i18n.t('banking.payments.delete_bulk_payment')}
              onClick={onRemoveBulkPayment}
            />
          )}
          {paymentExecution && <DownloadSepaAction paymentExecution={paymentExecution} />}
          {paymentExecution && paymentExecution.status == 'in_progress' && (
            <PopoverMenu.TouchableOption
              title={i18n.t('banking.payments.mark_as_paid')}
              onClick={onMarkAsPaid}
            />
          )}
        </PopoverMenu.Content>
      </PopoverMenu>
      <Button.Router
        to={navigation.finance.banking.payments.paymentId.fullPath({ id: payment.id })}
        type="secondary"
        iconName="arrow-right"
        disabled={!paymentExecution || !transfersCount}
      />
    </Box>
  )
}
